<template>
  <page-layout :hide-default-header="true">
    <!-- Forward the page-layout nav slot. Pages like Mijn omgeving render a
         left-hand section nav here, sitting beside the main content below
         the page header. -->
    <template v-if="$slots.nav" #nav>
      <slot name="nav" />
    </template>

    <!-- Render the rich page header into PageLayout's #header slot so it
         spans full width above the optional nav + main columns. Every
         page gets a subtle bottom divider beneath the header so the
         transition from chrome → content is consistent across the app.
         Dashboards with a `DashboardContextFilterBar` overlay used to
         draw their own border too — that's now stripped at the usage
         site to avoid a double line. -->
    <template v-if="!hideHeader" #header>
      <div class="border-b border-theme-border pb-4 dark:border-theme-border">
        <header class="flex flex-col gap-3 sm:gap-4">
          <!-- Top line: breadcrumbs sit on the same horizontal baseline as the
               meta slot (e.g. the token balance), so the meta chip aligns with
               the breadcrumbs rather than floating above the action buttons. -->
          <div
            v-if="resolvedBreadcrumbs.length || $slots.meta"
            class="flex items-center justify-between gap-3"
          >
            <div class="min-w-0">
              <AppBreadcrumbs :items="resolvedBreadcrumbs" />
            </div>
            <div v-if="$slots.meta" class="flex shrink-0 items-center justify-end gap-2">
              <slot name="meta" />
            </div>
          </div>

          <!-- Main line: identity (icon + title + subtitle) on the left, actions
               on the right. Bottom-aligned (sm:items-end) so the action buttons
               line up with the bottom of the subtitle. -->
          <div
            :class="[
              'flex flex-col gap-3 sm:gap-4',
              $slots.tabs
                ? 'md:flex-row md:flex-wrap md:items-end md:justify-between'
                : 'sm:flex-row sm:items-end sm:justify-between',
            ]"
          >
            <div class="flex min-w-0 flex-1 items-start gap-3 sm:gap-4 md:flex-[1_1_24rem]">
              <span
                v-if="resolvedIcon"
                class="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-md
                  bg-theme-palette-emerald/10 text-theme-palette-emerald
                  dark:bg-theme-palette-emerald/30 dark:text-theme-palette-emerald"
                aria-hidden="true"
              >
                <icon :path="resolvedIcon" size="22" />
              </span>

              <div class="min-w-0 flex-1">
                <span
                  v-if="resolvedEyebrow && !resolvedBreadcrumbs.length"
                  class="block font-azonix text-[10px] tracking-[0.18em] text-theme-palette-emerald dark:text-theme-palette-emerald uppercase mb-1"
                >
                  {{ resolvedEyebrow }}
                </span>
                <div class="flex items-center gap-2">
                  <h1 class="text-xl font-semibold tracking-tight text-theme-text">
                    {{ title }}
                  </h1>
                  <ToolStageBadge :stage="tool?.stage" />
                  <FavoriteToggle
                    v-if="favoritableDescriptor"
                    :id="favoritableDescriptor.id"
                    :kind="favoritableDescriptor.kind"
                    :label="favoritableDescriptor.label"
                    :to="favoritableDescriptor.to"
                    :icon="favoritableDescriptor.icon"
                    :size="14"
                    compact
                    class="flex-shrink-0"
                  />
                </div>
                <p v-if="subtitle" class="mt-1 text-sm text-theme-text-secondary max-w-2xl">
                  {{ subtitle }}
                </p>
              </div>
            </div>

            <div
              v-if="$slots.actions"
              class="flex flex-wrap items-center justify-end gap-2 sm:shrink-0"
            >
              <slot name="actions" />
            </div>

            <div
              v-if="$slots.tabs"
              class="min-w-0 w-full md:ml-auto md:w-max md:max-w-full md:self-end"
            >
              <slot name="tabs" />
            </div>
          </div>
        </header>
      </div>
    </template>

    <!-- Two-column layout when an inputs / side-config slot is provided. -->
    <div :class="hasSideConfig ? 'flex flex-col lg:flex-row gap-4' : ''">
      <aside v-if="hasSideConfig" class="lg:w-[24rem] lg:flex-shrink-0 order-1 lg:order-none">
        <slot name="inputs">
          <slot name="sideConfig" />
        </slot>
      </aside>
      <section
        :class="[
          hasSideConfig ? 'flex-1 min-w-0 space-y-4 order-2 lg:order-none' : 'space-y-4',
          contentClass,
        ]"
      >
        <slot />
      </section>
    </div>
  </page-layout>
</template>

<script setup>
  /* eslint-disable no-undef */
  import FavoriteToggle from '@/components/ui/buttons/FavoriteToggle.vue';
  import Icon from '@/components/Icon';
  import PageLayout from '@/components/ui/layout/PageLayout.vue';
  import AppBreadcrumbs from '@/components/ui/nav/AppBreadcrumbs.vue';
  import ToolStageBadge from '@/components/ui/feedback/ToolStageBadge.vue';
  import { useActiveCauldron } from '@/composables/useActiveCauldron';
  import { getCategoryMeta, getToolByKey } from '@/config/domain/toolKeys';
  import { computed, useSlots } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useRoute } from 'vue-router';

  const props = defineProps({
    title: { type: String, required: true },
    hideHeader: { type: Boolean, default: false },
    subtitle: { type: String, default: '' },
    backTo: { type: [String, Object], default: '/tools' },
    backLabel: { type: String, default: '' },
    showBackLink: { type: Boolean, default: true },
    // Category key (e.g. 'forecasts'). When provided, the eyebrow and default
    // icon are derived from CATEGORY_META in toolKeys.js.
    category: { type: String, default: '' },
    // Tool key (e.g. 'day-ahead-prediction'). When provided, takes precedence
    // over `category` for icon resolution and falls back to the tool's category
    // for the eyebrow.
    toolKey: { type: String, default: '' },
    // Explicit overrides (mdi path).
    icon: { type: String, default: '' },
    eyebrow: { type: String, default: '' },
    // Explicit breadcrumb override. Pass `false` to suppress breadcrumbs.
    breadcrumbs: { type: [Array, Boolean, Function], default: undefined },
    contentClass: { type: [String, Array, Object], default: '' },
    // Favoritable descriptor. When `null` the star is hidden; when omitted the
    // component falls back to `route.meta.favoritable` (mirroring the previous
    // PageHeader behaviour). Pass an object `{ id, kind, label, to, icon }`
    // to override.
    favoritable: { type: [Object, Boolean], default: undefined },
  });

  const slots = useSlots();
  const i18n = useI18n();
  const t = i18n.t;
  // Some test environments (and older vue-i18n setups) don't expose `te`.
  // Treat a missing checker as "key exists" so eyebrow translation always runs.
  const te = typeof i18n.te === 'function' ? i18n.te : () => true;

  // `useRoute()` returns undefined in component tests that don't mount the
  // router — guard for that so the test envs render without setting up a full
  // router.
  const route = useRoute() || {};

  const tool = computed(() => (props.toolKey ? getToolByKey(props.toolKey) : null));
  const categoryKey = computed(() => props.category || tool.value?.category || '');
  const categoryMeta = computed(() => getCategoryMeta(categoryKey.value));
  // Active cauldron is the lowest-priority eyebrow fallback — every page
  // belongs to one of the three cauldrons (or none), and on pages without
  // a more specific eyebrow we surface the cauldron label so the user
  // always sees which environment they're in.
  const { activeCauldron } = useActiveCauldron();

  // Resolution priority (icon + eyebrow):
  //   1. Explicit prop on the component
  //   2. Derived from `toolKey` / `category` (tools + dashboard pages)
  //   3. `route.meta.icon` / `route.meta.eyebrow` (centralised in router)
  // This lets generic pages (Home, Alerts, Catalog, …) ship a consistent
  // header by setting their icon once in the route definition.
  const resolvedIcon = computed(() => {
    if (props.icon) return props.icon;
    if (tool.value?.icon) return tool.value.icon;
    if (categoryMeta.value?.icon) return categoryMeta.value.icon;
    if (route.meta?.icon) return route.meta.icon;
    return '';
  });

  const resolvedEyebrow = computed(() => {
    // Priority: explicit prop > toolKey/category > route.meta.eyebrow(Key) >
    // active cauldron label. Tool pages already have a tighter category
    // eyebrow ("FORECASTS", "BENCHMARKS", …) so the cauldron fallback only
    // kicks in for News / Forward / Flex / Mijn-omgeving / Alerts where no
    // narrower eyebrow is set.
    if (props.eyebrow) return props.eyebrow;
    const meta = categoryMeta.value;
    if (meta) {
      if (te(meta.eyebrowKey)) return t(meta.eyebrowKey);
      return categoryKey.value.toUpperCase();
    }
    if (route.meta?.eyebrowKey && te(route.meta.eyebrowKey)) return t(route.meta.eyebrowKey);
    if (route.meta?.eyebrow) return route.meta.eyebrow;
    if (activeCauldron.value?.labelKey && te(activeCauldron.value.labelKey)) {
      return t(activeCauldron.value.labelKey).toUpperCase();
    }
    return '';
  });

  const hasSideConfig = computed(() => !!slots.inputs || !!slots.sideConfig);

  function toPath(to) {
    if (!to) return '';
    if (typeof to === 'string') return to.split(/[?#]/)[0];
    if (typeof to.path === 'string') return to.path;
    return '';
  }

  function isToCurrentRoute(to) {
    if (!to || !route.path) return false;
    if (typeof to === 'object' && to.name && route.name) return to.name === route.name;
    return toPath(to) === route.path;
  }

  function normalizeLabel(value) {
    return value == null ? '' : String(value).trim();
  }

  function labelsMatch(left, right) {
    return normalizeLabel(left).toLocaleLowerCase() === normalizeLabel(right).toLocaleLowerCase();
  }

  function normalizeCrumb(crumb) {
    if (!crumb) return null;
    const label = normalizeLabel(crumb.label || crumb.title);
    if (!label) return null;
    return {
      ...crumb,
      label,
      current: crumb.current === true || isToCurrentRoute(crumb.to),
    };
  }

  function resolveRawBreadcrumbs() {
    const source = props.breadcrumbs !== undefined ? props.breadcrumbs : route.meta?.breadcrumbs;
    if (source === false) return [];
    const items = typeof source === 'function' ? source(route, t) : source;
    if (!Array.isArray(items)) return [];
    return items.map(normalizeCrumb).filter(Boolean);
  }

  function dedupeConsecutiveCrumbs(items) {
    return items.filter((item, index) => {
      const previous = items[index - 1];
      if (!previous) return true;
      return !(labelsMatch(previous.label, item.label) && toPath(previous.to) === toPath(item.to));
    });
  }

  function buildToolBreadcrumbs(raw) {
    const allToolsCrumb = {
      label: t('ui.tool_page_back_short'),
      to: '/tools',
      current: false,
    };

    // Some tools have their own multi-page hierarchy. Those routes opt out of
    // the collection category crumb so their route metadata remains the
    // authoritative hierarchy after the shared "All tools" root.
    if (route.meta?.hideToolCategoryBreadcrumb) {
      return dedupeConsecutiveCrumbs([allToolsCrumb, ...raw.slice(1)]);
    }

    const category = categoryMeta.value;
    if (!category) return raw;

    const categoryCrumb = {
      label: category.titleKey && te(category.titleKey) ? t(category.titleKey) : category.key,
      to: { path: '/tools', query: { category: category.key } },
      current: false,
    };

    let rest = raw.slice(1);
    if (rest.length === 1 && rest[0].current && labelsMatch(rest[0].label, props.title)) {
      rest = [];
    }

    return dedupeConsecutiveCrumbs([allToolsCrumb, categoryCrumb, ...rest]);
  }

  const resolvedBreadcrumbs = computed(() => {
    const raw = resolveRawBreadcrumbs();
    if (categoryKey.value) return buildToolBreadcrumbs(raw);
    if (raw.length) return raw;
    if (props.showBackLink && props.backTo) {
      return [
        {
          label: props.backLabel || t('ui.tool_page_back_short'),
          to: props.backTo,
          current: false,
        },
      ];
    }
    if (activeCauldron.value?.labelKey && te(activeCauldron.value.labelKey)) {
      return [
        {
          label: t(activeCauldron.value.labelKey),
          to: activeCauldron.value.defaultPath || null,
          current: isToCurrentRoute(activeCauldron.value.defaultPath),
        },
      ];
    }
    return [];
  });

  // Resolve the favoritable descriptor used by the inline star button.
  // Priority: explicit prop (object or false) > `route.meta.favoritable` >
  // sensible default derived from the page title + current path.
  const favoritableDescriptor = computed(() => {
    const explicit = props.favoritable;
    if (explicit === null || explicit === false) return null;
    // Routes can opt out via `meta.favoritable: false` (e.g. the home page).
    if (route.meta?.favoritable === false || route.meta?.favoritable === null) return null;
    let raw = explicit !== undefined ? explicit : route.meta?.favoritable;
    if (!raw && raw !== true) {
      // Tool pages default to favoritable using the page title + current path.
      raw = true;
    }
    const config = typeof raw === 'function' ? raw(route) || {} : raw === true ? {} : raw;
    const path = route.path || route.fullPath;
    if (!path && !config.id && !config.to) return null;
    // Backend favorite ids must start with [a-z0-9] — a raw route path ("/x")
    // would be rejected with a 422, making the star silently do nothing.
    const id = config.id || path.replace(/^\/+/, '');
    const label = config.label || props.title || route.meta?.title || path;
    const to = config.to || path;
    const kind = config.kind || 'tool';
    const icon = config.icon || resolvedIcon.value || null;
    return { id, kind, label, to, icon };
  });
</script>
